import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import Cookies from 'js-cookie'

import axiosInstance from 'axiosApi'
import { history } from 'app/utils/app/history'
import { getHasChoiceManager, ROLE_MANAGER } from 'app/utils/app/user'
import { api, cookies, urls } from 'app/utils/constants'

import { KonsoleUser } from 'app/types/users/konsole'

const DEFAULT_USER: KonsoleUser = {
    id: -1,
    email: '',
    username: '',
    first_name: '',
    last_name: '',
    is_superuser: false,
    is_staff: false,
    groups: [],
    profil: {
        client: [],
    },
}

type UserStore = {
    /** User logged. */
    user: KonsoleUser

    // TODO: Remove when the backend has created rights for everything.
    isManager: boolean

    /** Know if the user can select the manager. */
    hasChoiceManager: boolean

    /** Update the user logged. */
    updateUser: (userData?: KonsoleUser) => Promise<void>

    /** Know if the user has the permission. */
    hasPermission: (codenamePermission: string) => boolean

    /** Logout the user. */
    logout: (wantsRemoveCookies?: boolean) => void
}

export const useUserStore = create<UserStore>()(
    immer((set, get) => ({
        user: DEFAULT_USER,

        isManager: false,

        hasChoiceManager: false,

        updateUser: async (userData) => {
            const { logout } = get()

            if (userData) {
                set((state) => {
                    state.user = userData

                    state.isManager = userData.groups.some(
                        (profil) => profil.name === ROLE_MANAGER,
                    )

                    state.hasChoiceManager = getHasChoiceManager(userData)
                })

                return
            }

            const requestUser = await axiosInstance
                .get<KonsoleUser>(api.auth.ME)
                .catch(() => {
                    /* Empty */
                })

            // Not logged
            if (!requestUser) {
                logout(false)

                window.location.reload()

                return
            }

            // Logged
            set((state) => {
                state.user = requestUser.data

                state.isManager = requestUser.data.groups.some(
                    (profil) => profil.name === ROLE_MANAGER,
                )

                state.hasChoiceManager = getHasChoiceManager(requestUser.data)
            })
        },

        hasPermission: (codenamePermission) => {
            const { user } = get()

            const permissionFound = user.groups.some((profil) =>
                profil.permissions.some(
                    (permission) => permission.codename === codenamePermission,
                ),
            )

            return permissionFound
        },

        logout: (wantsRemoveCookies = true) => {
            set((state) => {
                state.user = DEFAULT_USER
                state.hasChoiceManager = false
                state.isManager = false
            })

            if (wantsRemoveCookies) {
                Cookies.remove(cookies.ACCESS_TOKEN, cookies.OPTIONS)
                Cookies.remove(cookies.REFRESH_TOKEN, cookies.OPTIONS)
            }

            history.push(urls.auth.LOGIN)
        },
    })),
)
